import { useEffect, useRef, useState } from 'react';
import { env } from '@/shared/config/env';
import { useReducedMotion } from './useReducedMotion';
import {
  TICK_MS, CLOCK_MS, FLASH_EVERY_TICKS, FLASH_MS, MAX_POINTS,
  JITTER_SIGMA_FRAC, valueBounds, computeStatus, gaussian, clamp,
} from '../liveModeMeta';

const r1 = (n) => Math.round(n * 10) / 10;
const seedBuffer = (series) => (Array.isArray(series) ? series.slice(-MAX_POINTS) : []);

function enrichFromSeed(a) {
  return { ...a, liveValue: a.value, status: computeStatus(a.value, a), buffer: seedBuffer(a.series) };
}

function jitterOne(a) {
  const { lo, hi } = valueBounds(a);
  const band = (hi - lo) || Math.abs(a.liveValue) || 1;
  const sigma = band * JITTER_SIGMA_FRAC;
  const next = r1(clamp(a.liveValue + gaussian() * sigma, lo, hi));
  const buffer = [...a.buffer, next].slice(-MAX_POINTS);
  return { ...a, liveValue: next, status: computeStatus(next, a), buffer };
}

/**
 * The motion engine behind Live Mode. PURE with respect to data: it receives the
 * already-fetched live arrays (the page owns the queries — feature folders may
 * not import other features) and copies them into local state, so it never
 * mutates the shared react-query cache and the Recommendations/Alerts pages
 * can't flicker. The local copy re-seeds whenever a new array reference arrives
 * (i.e. the real 60s refetch), so synthetic drift gently self-corrects.
 *
 * Synthetic value jitter runs ONLY when MSW is enabled (a demo/dev build) and
 * the user hasn't requested reduced motion. Against a real backend there is no
 * jitter — we must never render fabricated sensor readings to an operator.
 *
 * @param {object[]|undefined} seedAlerts  live alert objects (with bands + series)
 * @param {object[]|undefined} seedRecs    live recommendation objects
 * @returns {{ alerts, recs, clock, flashId, jitterActive }}
 */
export function useLiveSimulator(seedAlerts, seedRecs) {
  const reduced = useReducedMotion();
  const jitterActive = env.ENABLE_MSW && !reduced;

  const [alerts, setAlerts] = useState([]);
  const [recs, setRecs] = useState([]);
  const [clock, setClock] = useState(() => new Date());
  const [flashId, setFlashId] = useState(null);
  // When the live values were last refreshed — drives the global freshness clock.
  // Updates on every real reseed (prod refetch) and every demo jitter tick.
  const [dataAsOf, setDataAsOf] = useState(() => new Date());

  // Mirror the latest alerts so the tick can read them without nesting side
  // effects inside a setState updater (updaters must stay pure).
  const alertsRef = useRef(alerts);
  useEffect(() => { alertsRef.current = alerts; }, [alerts]);

  // Seed (replace, never append) from the incoming live arrays.
  useEffect(() => {
    setAlerts(Array.isArray(seedAlerts) ? seedAlerts.map(enrichFromSeed) : []);
    setDataAsOf(new Date());
  }, [seedAlerts]);

  useEffect(() => {
    setRecs(Array.isArray(seedRecs) ? seedRecs : []);
  }, [seedRecs]);

  // Live clock (presentation only, always on; skips work when tab hidden).
  useEffect(() => {
    const id = setInterval(() => {
      if (document.hidden) return;
      setClock(new Date());
    }, CLOCK_MS);
    return () => clearInterval(id);
  }, []);

  // Synthetic jitter + periodic alert flash — demo only.
  const tickRef = useRef(0);
  const flashTimers = useRef(new Set());
  useEffect(() => {
    if (!jitterActive) return undefined;
    const id = setInterval(() => {
      if (document.hidden) return;
      tickRef.current += 1;
      setAlerts((prev) => prev.map(jitterOne));
      setDataAsOf(new Date());

      if (tickRef.current % FLASH_EVERY_TICKS === 0) {
        const list = alertsRef.current;
        if (list.length) {
          const pick = list[Math.floor(Math.random() * list.length)].id;
          setFlashId(pick);
          const t = setTimeout(() => {
            setFlashId((cur) => (cur === pick ? null : cur));
            flashTimers.current.delete(t);
          }, FLASH_MS);
          flashTimers.current.add(t);
        }
      }
    }, TICK_MS);

    const timers = flashTimers.current;
    return () => {
      clearInterval(id);
      timers.forEach(clearTimeout);
      timers.clear();
    };
  }, [jitterActive]);

  return { alerts, recs, clock, flashId, jitterActive, dataAsOf };
}
